import React, { useState } from 'react';

import { Tooltip } from 'antd';

import { highlightHtmlString, sanitizeDescription } from '@/shared/lib/sanitize';
import { extractFirstHtmlBlock, stripHtmlTags } from '@/shared/lib/text';
import { highlightText } from '@/shared/lib/text/highlight';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import styles from './AssetDescription.module.css';

/**
 * 자산 설명 컴포넌트 Props
 */
export type AssetDescriptionProps = {
  /** 표시할 설명 HTML 문자열 */
  description?: string;
  /** 표시 모드: tooltip (기본, Tooltip으로 표시) / expandable (펼치기/접기 버튼) */
  mode?: 'tooltip' | 'expandable';
  /** 툴팁 최대 너비 (기본값: 500px) */
  maxTooltipWidth?: string;
  /** 툴팁 배치 위치 (기본값: bottomLeft) */
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  /** "자세히 보기" 텍스트 커스터마이징 (기본값: "... 자세히 보기") */
  moreText?: string;
  /** "간단히 보기" 텍스트 커스터마이징 (expandable 모드, 기본값: "간단히 보기") */
  lessText?: string;
  /** 검색어 (하이라이트용) */
  highlightQuery?: string;
  /** 하이라이트 클래스명 */
  highlightClassName?: string;
  /** 편집 가능 여부 */
  editable?: boolean;
  /** 편집 버튼 클릭 핸들러 */
  onEdit?: () => void;
};

/**
 * 자산의 설명을 표시하는 공통 컴포넌트
 *
 * @description
 * HTML 설명을 sanitize하고, 두 가지 모드로 표시합니다:
 * 1. **tooltip 모드** (기본): 순수 텍스트 미리보기 + Tooltip으로 전체 HTML
 * 2. **expandable 모드**: 2줄 제한 + "더 보기"/"간단히 보기" 버튼으로 펼치기/접기
 *
 * **주요 기능:**
 * - XSS 방지를 위한 HTML sanitization
 * - tooltip 모드: 순수 텍스트 미리보기, Tooltip으로 전체 HTML 표시
 * - expandable 모드: 2줄 제한, 버튼으로 인라인 펼치기/접기
 * - 링크는 새 탭에서 열림 (`target="_blank"`)
 *
 * @example
 * ```tsx
 * // tooltip 모드 (기본)
 * <AssetDescription description={assetData?.description} />
 * ```
 *
 * @example
 * ```tsx
 * // expandable 모드 (테이블에서 사용)
 * <AssetDescription
 *   description={record.description}
 *   mode="expandable"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // 커스터마이징
 * <AssetDescription
 *   description={assetData?.description}
 *   mode="expandable"
 *   moreText="더 보기"
 *   lessText="접기"
 * />
 * ```
 */
export const AssetDescription: React.FC<AssetDescriptionProps> = ({
  description,
  mode = 'tooltip',
  maxTooltipWidth = '500px',
  tooltipPlacement = 'bottomLeft',
  moreText = '... 자세히 보기',
  lessText = '간단히 보기',
  highlightQuery = '',
  highlightClassName,
  editable = false,
  onEdit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const sanitized = description ? sanitizeDescription(description) : '';
  const highlightedHtml =
    sanitized && highlightQuery ? highlightHtmlString(sanitized, highlightQuery, highlightClassName) : sanitized;

  const EditButton = () => (
    <IconButton size="small" onClick={onEdit} tooltip="설명 편집">
      <Icon name="edit" size={16} />
    </IconButton>
  );

  if (!description) {
    return (
      <div className={styles.descriptionEmptyWrapper}>
        <div className={styles.descriptionEmpty}>-</div>
        {editable && onEdit && <EditButton />}
      </div>
    );
  }

  if (mode === 'expandable') {
    const { firstBlock, hasMore } = extractFirstHtmlBlock(highlightedHtml);
    const firstBlockPlainText = stripHtmlTags(firstBlock);

    return (
      <div className={styles.descriptionWrapperWithEdit}>
        <div className={styles.descriptionWrapper}>
          {isExpanded ? (
            <div className={styles.descriptionExpanded} dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
          ) : (
            <div className={styles.descriptionCollapsed}>{firstBlockPlainText}</div>
          )}
          {hasMore && (
            <span className={styles.toggleButton} onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? lessText : moreText}
            </span>
          )}
        </div>
        {editable && onEdit && <EditButton />}
      </div>
    );
  }

  const { firstBlock, hasMore } = extractFirstHtmlBlock(sanitized);

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = firstBlock;
  const plainTextPreview = tempDiv.textContent || tempDiv.innerText || '';

  const fullTextDiv = document.createElement('div');
  fullTextDiv.innerHTML = sanitized;
  const fullText = fullTextDiv.textContent || fullTextDiv.innerText || '';
  const hasActuallyMore = hasMore && plainTextPreview.trim() !== fullText.trim();

  const highlightedPreview = highlightQuery
    ? highlightText(plainTextPreview, highlightQuery, { className: highlightClassName })
    : plainTextPreview;

  const tooltipContent = highlightQuery
    ? highlightHtmlString(sanitized, highlightQuery, highlightClassName)
    : sanitized;

  return (
    <div className={styles.descriptionWrapperWithEdit}>
      <div className={styles.descriptionWrapper}>
        <p className={styles.description}>
          {highlightedPreview}
          {hasActuallyMore && (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: tooltipContent }} />}
              placement={tooltipPlacement}
              styles={{
                root: { maxWidth: maxTooltipWidth },
              }}
              classNames={{
                root: styles.descriptionTooltip,
              }}>
              <span className={styles.descriptionMore}>{moreText}</span>
            </Tooltip>
          )}
        </p>
      </div>
      {editable && onEdit && <EditButton />}
    </div>
  );
};
